/**
 * `/programs` — §11.9.
 */
import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  DEFAULT_MAX_CARRY_OVER,
  HIFZ_METHODS,
  TOTAL_PAGES,
  adherence,
  currentSegment,
  dayStamp,
  scopePages,
  type HifzMethod,
  type Meta,
  type Program,
  type ProgramScope,
  type VolumeUnit,
} from '@mushaf/corpus'
import {
  UNIT_LABELS,
  describeNew,
  describeOld,
  describeRecent,
  methodCopy,
} from '../content/methods'
import { SecondaryShell } from '../components/SecondaryShell'
import { WardPanel, describeScope } from '../components/ward/WardPanel'
import { selectActiveProgram, useLibrary } from '../stores/library'
import { useMeta } from '../lib/queries'
import { useNow } from '../lib/useNow'
import { Button, Chip, SectionTitle, Stepper } from '../components/ui'
import { IconTrash } from '../components/ui/icons'

export const Route = createFileRoute('/programs')({ component: ProgramsPage })

function ProgramsPage() {
  const { data: meta } = useMeta()
  const active = useLibrary(selectActiveProgram)
  const programs = useLibrary((s) => s.programs)
  const [configuring, setConfiguring] = useState<HifzMethod | null>(null)

  const saved = Object.values(programs).filter((p) => p.id !== active?.id)

  return (
    <SecondaryShell title="البرامج">
      {active && <WardPanel meta={meta} />}
      {active && meta && <ActiveCard program={active} meta={meta} />}

      <section className="grid gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
        <SectionTitle>{active ? 'برامج أخرى' : 'اختر برنامجًا'}</SectionTitle>
        <p className="text-xs leading-6 text-[var(--ink-faint)]">
          كل برنامج هنا هو منهج معروف للحفظ والمراجعة. الفرق بينها ثلاثة أرقام: مقدار الجديد، وسعة
          المراجعة القريبة، وكيف تُقسَّم المراجعة البعيدة.
        </p>

        <div className="grid gap-2 md:grid-cols-2">
          {HIFZ_METHODS.map((method) => (
            <article
              key={method.id}
              className="grid gap-1 rounded-xl border border-[var(--line)] p-3"
            >
              <h3 className="text-sm font-semibold">{methodCopy(method.id).name}</h3>
              <p className="text-xs leading-6 text-[var(--ink-soft)]">
                {methodCopy(method.id).blurb}
              </p>
              <dl className="grid gap-0.5 text-[11px] text-[var(--ink-faint)]">
                <Spec term="الجديد" value={describeNew(method)} />
                <Spec term="القريبة" value={describeRecent(method.recent)} />
                <Spec term="البعيدة" value={describeOld(method.old)} />
              </dl>
              <p className="text-[10px] text-[var(--ink-faint)]">
                المصدر: {methodCopy(method.id).source}
              </p>
              <Button
                tone="soft"
                className="mt-1 w-fit text-xs"
                onClick={() => setConfiguring(configuring?.id === method.id ? null : method)}
              >
                {configuring?.id === method.id ? 'إغلاق' : 'إعداد'}
              </Button>

              {configuring?.id === method.id && meta && (
                <ConfigureForm method={method} meta={meta} onDone={() => setConfiguring(null)} />
              )}
            </article>
          ))}
        </div>
      </section>

      {saved.length > 0 && meta && <SavedPrograms programs={saved} meta={meta} />}
    </SecondaryShell>
  )
}

function Spec({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex gap-1">
      <dt className="shrink-0 font-semibold">{term}:</dt>
      <dd>{value}</dd>
    </div>
  )
}

function ActiveCard({ program, meta }: { program: Program; meta: Meta }) {
  const saveProgram = useLibrary((s) => s.saveProgram)
  const setActiveProgram = useLibrary((s) => s.setActiveProgram)
  const now = useNow()
  const a = adherence(program, meta, now)
  const restLabel =
    program.restDays.length === 0
      ? 'لا أيام راحة'
      : `راحة: ${program.restDays.map((d) => DAY_NAMES[d]).join('، ')}`

  return (
    <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">{program.name}</h2>
        <span className="text-xs text-[var(--ink-faint)]">{methodCopy(program.methodId).name}</span>
        {program.state === 'paused' && <Chip tone="gold">موقوف مؤقتًا</Chip>}
        <div className="flex-1" />
        <Button
          tone="plain"
          className="text-xs"
          onClick={() =>
            void saveProgram({
              ...program,
              state: program.state === 'paused' ? 'active' : 'paused',
            })
          }
        >
          {program.state === 'paused' ? 'استئناف' : 'إيقاف مؤقت'}
        </Button>
        <Button
          tone="plain"
          className="text-xs"
          title="إلغاء التفعيل دون حذف التقدّم"
          onClick={() => void setActiveProgram(null)}
        >
          إنهاء
        </Button>
      </div>

      <span
        role="progressbar"
        aria-valuenow={a.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="تقدّم البرنامج"
        className="h-2 overflow-hidden rounded-full bg-[var(--surface-2)]"
      >
        <span className="block h-full bg-[var(--gold)]" style={{ width: `${a.percent}%` }} />
      </span>

      <p className="text-xs leading-6 text-[var(--ink-soft)]">
        {describeScope(program.scope)} —{' '}
        <span dir="ltr">
          {a.percent}% ({Math.round(a.done / 15)} من {Math.round(a.total / 15)} صفحة)
        </span>
        . الموضع الحالي{' '}
        <span dir="ltr">
          صفحة {program.cursor.page} · سطر {program.cursor.line}
        </span>
        {a.finishesOn !== null &&
          `. الختام المتوقّع ${new Date(a.finishesOn).toISOString().slice(0, 10)}`}
      </p>

      <p className="text-[11px] text-[var(--ink-faint)]">
        {restLabel} · حدّ التأجيل {program.maxCarryOver} يوم
      </p>

      <VolumeEditor program={program} />
    </section>
  )
}

const DAY_NAMES = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']

/**
 * THE HISTORY LIST IS THE POINT OF THE FEATURE (§11.9): it makes the append-only
 * model visible, so a change reads as "I raised my ward on this date" rather than as
 * the app quietly restating how far behind you have always been.
 */
function VolumeEditor({ program }: { program: Program }) {
  const editVolume = useLibrary((s) => s.editVolume)
  const [showHistory, setShowHistory] = useState(false)
  const now = useNow()
  const today = dayStamp(now)
  const segment = currentSegment(program, today)
  const isHifz = program.kind === 'hifz'

  const history = useMemo(
    () => [...program.volumes].sort((a, b) => b.from - a.from),
    [program.volumes],
  )

  const update = (patch: Partial<{ unit: VolumeUnit; amount: number; review: number }>) =>
    void editVolume(
      program.id,
      { unit: segment.unit, amount: segment.amount, review: segment.review, ...patch },
      Date.now(),
    )

  return (
    <div className="grid gap-2 rounded-xl bg-[var(--surface-2)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs">المقدار اليومي</span>
        <Stepper
          label="المقدار اليومي"
          value={segment.amount}
          min={0}
          max={200}
          onChange={(amount) => update({ amount })}
        />
        <label className="flex items-center gap-1 text-xs">
          <span className="sr-only">وحدة المقدار</span>
          <select
            aria-label="وحدة المقدار"
            value={segment.unit}
            onChange={(e) => update({ unit: e.target.value as VolumeUnit })}
          >
            {(Object.keys(UNIT_LABELS) as VolumeUnit[]).map((u) => (
              <option key={u} value={u}>
                {UNIT_LABELS[u]}
              </option>
            ))}
          </select>
        </label>
        <span className="text-[11px] text-[var(--ink-faint)]">يمكن تعديله في أي وقت لاحقًا</span>
      </div>

      {isHifz && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">سقف المراجعة</span>
          <Stepper
            label="سقف المراجعة"
            value={segment.review}
            min={0}
            max={100}
            onChange={(review) => update({ review })}
          />
          <span className="text-[11px] text-[var(--ink-faint)]">
            أقصى عدد صفحات مراجعة في اليوم
          </span>
        </div>
      )}

      {program.volumes.length > 1 && (
        <div className="grid gap-1">
          <Button
            tone="ghost"
            className="w-fit text-[11px]"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((v) => !v)}
          >
            سجلّ المقدار ({program.volumes.length})
          </Button>
          {showHistory && (
            <>
              <p className="text-[11px] leading-5 text-[var(--ink-faint)]">
                كل تعديل يبدأ من تاريخه — ما مضى يبقى محسوبًا بالمقدار الذي كان ساريًا وقته.
              </p>
              <ul className="grid gap-0.5 text-[11px]">
                {history.map((v, i) => (
                  <li key={v.from} className="flex gap-2">
                    <span dir="ltr">
                      من {new Date(v.from * 86_400_000).toISOString().slice(0, 10)}
                    </span>
                    <span>
                      {v.amount} {UNIT_LABELS[v.unit]}
                    </span>
                    {i === 0 && <span className="text-[var(--gold)]">— الساري الآن</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * THE CONFIGURE FORM IS SEEDED ENTIRELY FROM THE METHOD, so pressing «ابدأ هذا
 * البرنامج» with nothing touched is already a valid plan. The form exists to NARROW a
 * method, not to make the reader assemble one from parts.
 */
function ConfigureForm({
  method,
  meta,
  onDone,
}: {
  method: HifzMethod
  meta: Meta
  onDone: () => void
}) {
  const startProgram = useLibrary((s) => s.startProgram)
  const [name, setName] = useState(methodCopy(method.id).name)
  const [scopeType, setScopeType] = useState<ProgramScope['type']>('mushaf')
  const [from, setFrom] = useState(1)
  const [to, setTo] = useState(scopeType === 'surah' ? 114 : scopeType === 'juz' ? 30 : TOTAL_PAGES)
  const [amount, setAmount] = useState(method.defaultVolume.amount)
  const [unit, setUnit] = useState<VolumeUnit>(method.defaultVolume.unit)
  const [review, setReview] = useState(method.defaultVolume.review)
  const [restDays, setRestDays] = useState<number[]>([])
  const [maxCarryOver, setMaxCarryOver] = useState(DEFAULT_MAX_CARRY_OVER)
  const [startPage, setStartPage] = useState<number | null>(null)

  const scope: ProgramScope =
    scopeType === 'mushaf' ? { type: 'mushaf' } : { type: scopeType, from, to }
  const bounds = scopePages(scope, meta)

  // The start page defaults to the scope's first page AND FOLLOWS IT until the reader
  // sets one; changing the scope type resets it, because a page chosen inside juz 30
  // means nothing in juz 1.
  const effectiveStart = startPage ?? bounds.from

  const setScopeKind = (next: ProgramScope['type']) => {
    setScopeType(next)
    setFrom(1)
    setTo(next === 'surah' ? 114 : next === 'juz' ? 30 : TOTAL_PAGES)
    setStartPage(null)
  }

  const submit = async () => {
    await startProgram({
      id: `p-${Date.now().toString(36)}`,
      methodId: method.id,
      name,
      scope,
      volume: { unit, amount, review },
      cursor: { page: effectiveStart, line: 1 },
      restDays,
      maxCarryOver,
      now: Date.now(),
    })
    onDone()
  }

  const max = scopeType === 'surah' ? 114 : scopeType === 'juz' ? 30 : TOTAL_PAGES

  return (
    <form
      className="mt-2 grid gap-3 rounded-xl bg-[var(--surface-2)] p-3"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <label className="grid gap-1 text-xs">
        <span>اسم البرنامج</span>
        <input
          className="rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div className="grid gap-1 text-xs">
        <label className="grid gap-1">
          <span>النطاق</span>
          <select
            aria-label="نوع النطاق"
            value={scopeType}
            onChange={(e) => setScopeKind(e.target.value as ProgramScope['type'])}
          >
            <option value="mushaf">مصحف</option>
            <option value="juz">أجزاء</option>
            <option value="surah">سور</option>
            <option value="pages">صفحات</option>
          </select>
        </label>

        {scopeType !== 'mushaf' && (
          <div className="flex flex-wrap items-center gap-2">
            <Stepper
              label="من"
              value={from}
              min={1}
              max={max}
              // Range pickers keep from <= to BY CONSTRUCTION.
              onChange={(n) => {
                setFrom(n)
                if (n > to) setTo(n)
                setStartPage(null)
              }}
            />
            <Stepper
              label="إلى"
              value={to}
              min={from}
              max={max}
              onChange={(n) => setTo(Math.max(from, n))}
            />
            <span dir="ltr" className="text-[11px] text-[var(--ink-faint)]">
              {bounds.to - bounds.from + 1} صفحة ({bounds.from}–{bounds.to})
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>المقدار اليومي</span>
        <Stepper label="المقدار اليومي" value={amount} min={0} max={200} onChange={setAmount} />
        <select
          aria-label="وحدة المقدار اليومي"
          value={unit}
          onChange={(e) => setUnit(e.target.value as VolumeUnit)}
        >
          {(Object.keys(UNIT_LABELS) as VolumeUnit[]).map((u) => (
            <option key={u} value={u}>
              {UNIT_LABELS[u]}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-[var(--ink-faint)]">يمكن تعديله في أي وقت لاحقًا</span>
      </div>

      {method.kind === 'hifz' && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span>سقف المراجعة</span>
          <Stepper label="سقف المراجعة" value={review} min={0} max={100} onChange={setReview} />
          <span className="text-[11px] text-[var(--ink-faint)]">
            أقصى عدد صفحات مراجعة في اليوم
          </span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>صفحة البداية</span>
        <Stepper
          label="صفحة البداية"
          value={effectiveStart}
          min={bounds.from}
          max={bounds.to}
          onChange={setStartPage}
        />
        <span dir="ltr" className="text-[11px] text-[var(--ink-faint)]">
          ضمن {bounds.from}–{bounds.to}
        </span>
      </div>

      <fieldset className="grid gap-1 text-xs">
        <legend>أيام الراحة</legend>
        <div className="flex flex-wrap gap-1">
          {DAY_NAMES.map((label, day) => (
            <Button
              key={day}
              tone={restDays.includes(day) ? 'gold' : 'plain'}
              className="px-2 py-1 text-[11px]"
              aria-pressed={restDays.includes(day)}
              onClick={() =>
                setRestDays((prev) =>
                  prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort(),
                )
              }
            >
              {label}
            </Button>
          ))}
        </div>
        <span className="text-[11px] text-[var(--ink-faint)]">
          لا يُحتسب عليك ورد في هذه الأيام، ولا تُعدّ تأخيرًا.
        </span>
      </fieldset>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span>حدّ التأجيل</span>
        <Stepper
          label="حدّ التأجيل"
          value={maxCarryOver}
          min={0}
          max={30}
          onChange={setMaxCarryOver}
        />
        <span className="text-[11px] leading-5 text-[var(--ink-faint)]">
          أكثر ما يُرحَّل من ورد فائت، مقيسًا بعدد الأيام. الحدّ موجود حتى لا يتحوّل أسبوع متعثّر
          إلى يوم لا يُطاق.
        </span>
      </div>

      <Button tone="gold" className="w-fit text-xs" onClick={() => void submit()}>
        ابدأ هذا البرنامج
      </Button>
      <p className="text-[11px] text-[var(--ink-faint)]">
        تفعيل برنامج جديد يوقف البرنامج الحالي ولا يحذف تقدّمه.
      </p>
    </form>
  )
}

function SavedPrograms({ programs, meta }: { programs: Program[]; meta: Meta }) {
  const setActiveProgram = useLibrary((s) => s.setActiveProgram)
  const deleteProgram = useLibrary((s) => s.deleteProgram)
  const now = useNow()

  return (
    <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <SectionTitle>البرامج المحفوظة</SectionTitle>
      <ul className="grid gap-1">
        {programs.map((p) => (
          <li key={p.id} className="flex flex-wrap items-center gap-2 rounded-xl p-1 text-xs">
            <span className="font-semibold">{p.name}</span>
            <span className="text-[var(--ink-faint)]">{describeScope(p.scope)}</span>
            <span dir="ltr" className="text-[var(--ink-faint)]">
              صفحة {p.cursor.page} · سطر {p.cursor.line}
            </span>
            {p.state === 'done' && <Chip tone="verse">مكتمل</Chip>}
            <span className="flex-1" />
            <span className="text-[10px] text-[var(--ink-faint)]">
              {adherence(p, meta, now).percent}%
            </span>
            <Button tone="soft" className="text-[11px]" onClick={() => void setActiveProgram(p.id)}>
              تفعيل
            </Button>
            <Button
              tone="ghost"
              className="text-[11px]"
              title="حذف البرنامج وتقدّمه"
              aria-label={`حذف ${p.name}`}
              onClick={() => void deleteProgram(p.id)}
            >
              <IconTrash size={14} />
            </Button>
          </li>
        ))}
      </ul>
    </section>
  )
}
